// Left column of the settings dialog: title, section list, and Log out pinned
// to the bottom. Geometry follows Figma node 700:948 (224px column, 12px radius
// rows, 20px icons, 14px/20px labels, selected row at white/12%).

import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { BuildVersionBadge } from '../../layout/BuildVersionBadge'
import type { SettingsSectionDefinition, SettingsSectionId } from './settingsSections'

interface SettingsNavProps {
  visible: boolean
  sections: SettingsSectionDefinition[]
  activeSection: SettingsSectionId
  onSelect: (id: SettingsSectionId) => void
  onSignOut: () => void
  /** Focus target on open — the shell focuses the active row. */
  activeItemRef: React.Ref<HTMLButtonElement>
  postpaid: boolean
}

export default function SettingsNav({
  visible,
  sections,
  activeSection,
  onSelect,
  onSignOut,
  activeItemRef,
  postpaid,
}: SettingsNavProps) {
  const { t } = useTranslation('settings')

  return (
    <div
      className="flex h-auto min-w-0 w-full shrink-0 flex-col border-r border-border-subtle bg-surface-1 sm:h-full sm:w-56"
      data-testid="settings-nav"
    >
      <div className="py-4 pl-6 pr-4">
        <h2 className="text-xl font-semibold leading-[25px] text-white">
          {t('settingsModal.title')}
        </h2>
      </div>

      <nav
        className="flex min-h-0 flex-1 flex-row flex-wrap gap-1 overflow-y-auto px-4 sm:flex-col sm:flex-nowrap"
        aria-label={t('settingsModal.title')}
      >
        {sections.map((section) => {
          const selected = section.id === activeSection
          const { Icon } = section
          return (
            <button
              key={section.id}
              ref={selected ? activeItemRef : undefined}
              type="button"
              // NOT role="tab": a tab needs an enclosing tablist, an
              // aria-controls'd panel and arrow-key roving focus. Claiming the
              // role without those gives a screen reader an interface that
              // does not behave as announced. These are navigation buttons, so
              // `aria-current` is the honest annotation.
              aria-current={selected ? 'page' : undefined}
              data-testid={`settings-nav-${section.id}`}
              onClick={() => onSelect(section.id)}
              className={cn(
                'flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-1 text-left transition-colors sm:w-full',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                selected ? 'bg-white/[0.12]' : 'hover:bg-white/[0.06]',
              )}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <Icon className="h-4 w-4 text-white" strokeWidth={2} />
              </span>
              <span className="min-h-6 flex-1 truncate text-sm leading-5 text-white">
                {t(postpaid && section.postpaidLabelKey ? section.postpaidLabelKey : section.labelKey)}
              </span>
            </button>
          )
        })}
      </nav>

      <div className="px-4 pb-4 pt-2">
        <BuildVersionBadge visible={visible} />
        <button
          type="button"
          data-testid="settings-nav-signout"
          onClick={onSignOut}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-xl px-2 py-1 text-left transition-colors hover:bg-white/[0.06]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          )}
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <LogOut className="h-4 w-4 text-white" strokeWidth={2} />
          </span>
          <span className="min-h-6 flex-1 truncate text-sm leading-5 text-white">
            {t('settingsModal.signOut')}
          </span>
        </button>
      </div>
    </div>
  )
}
